import './BankTransactionsList.scss'
import { useCallback, useEffect, useState } from "react"
import BankTransactionsListItem from "./BankTransactionsListItem"
import { countUserLabelMatches, createBankTransaction, destroyBankTransaction, destroyStatementLink, linkBankTransactions, getStatementBalance, listBankConnections, listBudgets, listBankTransactions, updateBankTransaction } from "../../ApiInterface"
import { accountPath, computeTotalAmount, computeTotalAmountWithImpact, formatCurrency, formatDate, generateNewBankTransaction, isFromBank, statementPeriod, statementTitle } from "../../Helpers"
import StatementsStrip from './StatementsStrip'
import LinkModal from './LinkModal'
import TricountEntryModal from './TricountEntryModal'
import Toast from '../../components/Toast'
import Modal from '../../components/Modal'
import AccountIcon from '../../components/AccountIcon'
import { useAccounts } from '../../AccountsContext'
import { Link, useNavigate, useParams } from 'react-router-dom'

interface PendingRename {
    bankTransaction: BankTransaction
    userLabel: string | null
    count: number
    resolve: (choice: 'all' | 'one' | 'cancel') => void
}

const BankTransactionsList = () => {
    const [bankTransactions, setBankTransactions] = useState([] as BankTransaction[])
    const [budgets, setBudgets] = useState([] as Budget[])
    const [newBankTransaction, setNewBankTransaction] = useState(null as NewBankTransaction | null)
    const [bankConnections, setBankConnections] = useState([] as BankConnection[])
    const [pendingRename, setPendingRename] = useState(null as PendingRename | null)
    const [statementBalance, setStatementBalance] = useState(null as BankBalance | null)
    const [sending, setSending] = useState(null as BankTransaction | null)
    const [linking, setLinking] = useState(null as { bankTransaction: BankTransaction, preselectedId?: number | null } | null)
    const [toast, setToast] = useState(null as { message: string, undo?: () => void, open?: () => void } | null)
    const closeToast = useCallback(() => setToast(null), [])

    const params = useParams()
    const navigate = useNavigate()
    const { filename } = params
    const accountId = params.accountId === undefined ? undefined : Number(params.accountId)
    const { accounts, loaded: accountsLoaded, reloadAccounts } = useAccounts()
    const account = accounts.find((account) => account.id === accountId)

    // Sans compte ni relevé dans l'URL : le premier compte, puis son relevé le plus récent. Un relevé
    // sans opération (nouveau relevé créé depuis la page Banque) s'ouvre aussi.
    useEffect(() => {
        if(!accountsLoaded) {
            return
        }
        if(account === undefined) {
            if(accounts.length > 0) {
                navigate(accountPath(accounts[0]), {replace: true})
            }
        }
        else if(filename === undefined) {
            navigate(accountPath(account, account.statements[0]?.filename ?? account.name), {replace: true})
        }
    }, [accountsLoaded, accounts, account, filename, navigate])

    const statements = account === undefined || filename === undefined || account.statements.some((statement) => statement.filename === filename)
        ? account?.statements || []
        : [{filename, count: 0, total_cents: 0, budget_cents: 0, to_categorize_count: 0, first_at: '', last_at: '', period: statementPeriod(filename)} as Statement, ...account.statements]
    const statement = statements.find((statement) => statement.filename === filename)

    // Tricount : ce qu'on vous doit (positif) ou ce que vous devez, sur tout le Tricount et pas le seul
    // relevé affiché, comme le solde de Tricount. Les opérations liées comptent, payées par vous.
    const owedCents = account?.tricount_balance_cents ?? 0
    const linkedCash = computeTotalAmount(bankTransactions.filter((bankTransaction) => bankTransaction.account_id !== accountId))

    const fetchBankTransactions = useCallback(() => {
        if(accountId !== undefined && filename !== undefined) {
            listBankTransactions({account_id: accountId, filename}).then((bankTransactions) => {
                setNewBankTransaction(null)
                setBankTransactions(bankTransactions)
            })
        }
    }, [accountId, filename])
    // Après une modification : les totaux du relevé et le reste à catégoriser du menu changent.
    const reload = useCallback(() => {
        fetchBankTransactions()
        reloadAccounts()
    }, [fetchBankTransactions, reloadAccounts])
    useEffect(() => {
        setStatementBalance(null)
        if(accountId !== undefined && filename !== undefined) {
            getStatementBalance(accountId, filename).then(setStatementBalance)
        }
    }, [accountId, filename])
    useEffect(() => {
        setBankTransactions([])
        fetchBankTransactions()
    }, [fetchBankTransactions])

    useEffect(() => {
        listBudgets().then(setBudgets)
    }, [])

    useEffect(() => {
        listBankConnections().then(setBankConnections)
    }, [])

    const insertNewBankTransaction = () => {
        if(accountId !== undefined) {
            setNewBankTransaction(generateNewBankTransaction(accountId, filename || '', account?.default_impact))
        }
    }

    const editBankTransaction = (bankTransaction: BankTransaction | NewBankTransaction, params: UpdateBankTransactionParams) => {
        if(!('id' in bankTransaction)) {
            // Dans un compte rangé par mois, l'opération va dans le relevé du mois de sa date.
            createBankTransaction(({
                ...bankTransaction,
                ...params
            })).then((created) => {
                reload()
                if(account && created.filename !== filename) {
                    setToast({
                        message: `Rangée dans « ${created.filename} », d'après sa date`,
                        open: () => navigate(accountPath(account, created.filename))
                    })
                }
            })
        }
        else {
            setBankTransactions(bankTransactions.map(t => {
                if(t.id === bankTransaction.id) {
                    return {
                        ...bankTransaction,
                        ...params
                    }
                }
                return t
            }))
            updateBankTransaction(bankTransaction.id, params).then(reload)
        }        
    }

    // Les autres opérations au même libellé banque exact et au même libellé perso (vide ou non) peuvent
    // être renommées aussi : une fenêtre demande lesquelles. Résout à false si l'utilisateur annule.
    const renameBankTransaction = (bankTransaction: BankTransaction, userLabel: string): Promise<boolean> => {
        const user_label = userLabel.trim() === '' || userLabel.trim() === bankTransaction.label ? null : userLabel.trim()
        const save = (propagate: boolean) => {
            setBankTransactions(bankTransactions => bankTransactions.map(t => t.id === bankTransaction.id
                ? {...t, user_label, user_label_inherited: false}
                : t))
            return updateBankTransaction(bankTransaction.id, {user_label}, propagate).then(fetchBankTransactions)
        }
        return countUserLabelMatches(bankTransaction.id).then(count => {
            if(count === 0) {
                return save(false).then(() => true)
            }
            return new Promise<boolean>(resolve => setPendingRename({
                bankTransaction,
                userLabel: user_label,
                count,
                resolve: (choice) => {
                    setPendingRename(null)
                    if(choice === 'cancel') {
                        resolve(false)
                    }
                    else {
                        save(choice === 'all').then(() => resolve(true))
                    }
                }
            }))
        })
    }

 // Retirer une fusion la défait : l'opération absorbée revient telle qu'elle était. Un lien d'avant
    // la copie (sans restorable) laisse la synchro Tricount réimporter la dépense à part.
    const removeLink = (bankTransaction: BankTransaction, link: StatementLink) => {
        const account = accounts.find((account) => account.id === link.account_id)
        const consequence = link.restorable
            ? `\n\nLes deux opérations redeviennent séparées, comme avant le lien.`
            : link.tricount_entry
                ? `\n\nLa dépense Tricount sera réimportée à part à la prochaine synchro, et comptée en plus de cette opération.`
                : ''
        if(window.confirm(`Retirer « ${bankTransaction.user_label || bankTransaction.label} » du relevé ${account?.name || ''} ?${consequence}`)) {
            destroyStatementLink(bankTransaction.id, link.id).then(reload)
        }
    }

    // Lien évident : fusion directe, annulable depuis la barre du bas.
    const quickLink = (bankTransaction: BankTransaction, suggestion: LinkSuggestion) => {
        const account = accounts.find((account) => account.id === suggestion.account_id)
        linkBankTransactions(bankTransaction.id, suggestion.id, {}).then((kept) => {
            reload()
            const link = kept.statement_links.find((link) => link.account_id === (kept.id === bankTransaction.id ? suggestion.account_id : bankTransaction.account_id))
            setToast({
                message: `Lié à « ${suggestion.label} » (${account?.name || 'autre compte'})`,
                undo: link && (() => destroyStatementLink(kept.id, link.id).then(() => {
                    setToast(null)
                    reload()
                }))
            })
        })
    }

    const removeBankTransaction = (bankTransaction: BankTransaction | NewBankTransaction) => {
        if(!('id' in bankTransaction)) {
            setNewBankTransaction(null)
        }
        else {
            setBankTransactions(bankTransactions.filter(t => t.id !== bankTransaction.id))
            destroyBankTransaction(bankTransaction.id).then(reloadAccounts)
        }
    }

    const toCategorizeCount = bankTransactions.filter((t) => !t.budget_id).length

    return <div className="bank-transactions-list">
            { toast && <Toast message={toast.message}
                actionLabel={toast.undo ? 'Annuler' : toast.open && 'Voir'}
                onAction={toast.undo || (toast.open && (() => { toast.open?.(); setToast(null) }))}
                onClose={closeToast}/> }
            { linking && <LinkModal
                bankTransaction={linking.bankTransaction}
                preselectedId={linking.preselectedId}
                accounts={accounts}
                budgets={budgets}
                onCancel={() => setLinking(null)}
                onDone={() => { setLinking(null); reload() }}/> }
            { sending && <TricountEntryModal
                bankTransaction={sending}
                duplicateId={sending.link_suggestion?.id}
                onLinkInstead={(id) => { setSending(null); setLinking({ bankTransaction: sending, preselectedId: id }) }}
                onCancel={() => setSending(null)}
                onDone={() => { setSending(null); reload() }}/> }
            { pendingRename && <Modal onCancel={() => pendingRename.resolve('cancel')}>
                <div className="rename-form">
                    <h3>{ pendingRename.userLabel
                        ? <>Renommer en « {pendingRename.userLabel} »</>
                        : <>Retirer le libellé « {pendingRename.bankTransaction.user_label} »</> }</h3>
                    <p>
                        <b>{pendingRename.count} autre{pendingRename.count > 1 ? 's' : ''} opération{pendingRename.count > 1 ? 's' : ''}</b> ont
                        le même libellé banque « {pendingRename.bankTransaction.label} »
                        { pendingRename.bankTransaction.user_label
                            ? <> et le même libellé « {pendingRename.bankTransaction.user_label} ».</>
                            : <>, sans libellé perso.</> }
                    </p>
                    <div className="buttons">
                        <button className="tl-btn tl-btn-secondary" onClick={() => pendingRename.resolve('one')}>
                            { pendingRename.userLabel ? 'Renommer' : 'Retirer' } juste celle-ci
                        </button>
                        <button className="tl-btn tl-btn-primary" autoFocus onClick={() => pendingRename.resolve('all')}>
                            { pendingRename.userLabel ? 'Renommer' : 'Retirer' } aussi les {pendingRename.count} autre{pendingRename.count > 1 ? 's' : ''}
                        </button>
                    </div>
                </div>
            </Modal>}

            { bankConnections.some((bankConnection) => bankConnection['expires_soon?']) && <div className="tl-badge tl-badge-todo">
                L'accès à la banque expire bientôt : <Link to="/bank_connections">le renouveler</Link>
            </div> }

            { accountsLoaded && accounts.length === 0 && <div className="tl-card empty-accounts">
                Aucun compte pour l'instant : <Link to="/bank_connections">en créer un dans la page Banque</Link>
            </div> }

            { account && <div className="tl-page-head">
                <div className="account-title">
                    <AccountIcon account={account} size={40}/>
                    <div>
                        <h2>
                            { account.name }
                            { statement && (account.card || (account.monthly && statement.period)) && <span className="statement-title"> · { statementTitle(statement) }</span> }
                        </h2>
                        <div className="sub">
                            { statement && statement.first_at && <>{ formatDate(statement.first_at) } – { formatDate(statement.last_at) } · </> }
                            { bankTransactions.length } opérations
                            { statement && account.card && !statement.period && <> · { statement.filename }</> }
                        </div>
                    </div>
                </div>
            </div> }

            { account && filename && statements.length > 1 && <StatementsStrip account={account} statements={statements} filename={filename}/> }

            <div className={`tl-kpis ${account?.tricount ? '' : 'two'}`}>
                <div className="tl-card tl-kpi dark">
                    <div className="l">Impact sur les budgets</div>
                    <div className="n">{ formatCurrency(computeTotalAmountWithImpact(bankTransactions, budgets)) }</div>
                    <div className="hint">après ratio d'impact, hors transferts</div>
                </div>
                <div className="tl-card tl-kpi">
                    <div className="l">Impact cash</div>
                    <div className="n">{ formatCurrency(computeTotalAmount(bankTransactions)) }</div>
                    <div className="hint">
                        { statementBalance
                            ? <>encours banque{ statementBalance.reference_date && <> au { formatDate(statementBalance.reference_date) }</> }</>
                            : account?.tricount
                                ? <>payé ou reçu par vous{ linkedCash !== 0 && <>, dont { formatCurrency(linkedCash) } via un autre compte</> }</>
                                : 'toutes opérations confondues' }
                    </div>
                </div>
                { account?.tricount && <div className="tl-card tl-kpi">
                    <div className="l">{ owedCents < 0 ? 'Vous devez' : 'On vous doit' }</div>
                    <div className={`n ${owedCents > 0 ? 'owed' : owedCents < 0 ? 'owing' : ''}`}>{ formatCurrency(Math.abs(owedCents)) }</div>
                    <div className="hint">cash − budget, sur tout le Tricount</div>
                </div> }
            </div>

            <div className="toolbar">
                <div>
                    { toCategorizeCount > 0
                        ? <span className="tl-badge tl-badge-todo">{ toCategorizeCount } à catégoriser</span>
                        : bankTransactions.length > 0 && <span className="tl-badge tl-badge-ok">Tout est catégorisé</span> }
                </div>
                <div className="actions">
                    <button className="tl-btn tl-btn-primary" onClick={insertNewBankTransaction}>+ Nouvelle opération</button>
                </div>
            </div>

        {filename &&
            <div className="tl-card bank-transactions-card">
                <div className="bank-transaction-row bank-transaction-row-header">
                    <div className="date">Date</div>
                    <div className="label">Libellé</div>
                    <div className="amount">Montant</div>
                    <div className="category">Budget</div>
                    <div className="impact-ratio">Impact</div>
                    <div className="is-protected"></div>
                </div>
                { newBankTransaction && <BankTransactionsListItem
                        bankTransaction={newBankTransaction}
                        budgets={budgets}
                        onChange={(params) => {editBankTransaction(newBankTransaction, params)}}
                        onRename={() => Promise.resolve(false)}
                        onDelete={() => removeBankTransaction(newBankTransaction)}/>
                }
                { bankTransactions.map(bankTransaction =>
                    <BankTransactionsListItem
                        key={bankTransaction.id}
                        bankTransaction={bankTransaction}
                        budgets={budgets}
                        onChange={(params) => editBankTransaction(bankTransaction, params)}
                        onRename={(userLabel) => renameBankTransaction(bankTransaction, userLabel)}
                        onDelete={() => removeBankTransaction(bankTransaction)}
                        accounts={accounts}
                        accountId={accountId}
                        onLink={(preselectedId) => setLinking({ bankTransaction, preselectedId })}
                        onSendToTricount={bankTransaction.amount_cents > 0 && isFromBank(bankTransaction, accounts) && !account?.tricount
                            ? () => setSending(bankTransaction) : undefined}
                        onRemoveLink={(link) => removeLink(bankTransaction, link)}
                        onQuickLink={(suggestion) => quickLink(bankTransaction, suggestion)}
                        showCash={account?.tricount}/>)
                }
            </div>}
    </div>
}

export default BankTransactionsList
